import { noteModule } from "@rentiohq/shared-frontend/dist/reduxV2/note";
import { ELocale } from "@rentiohq/shared-frontend/dist/types/i18n.types";
import { INote } from "@rentiohq/shared-frontend/dist/types/notes.types";
import { formatDate } from "@rentiohq/shared-frontend/dist/utils/date.utils";
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";

export const SHARE_HEADING_KEY = "application.send_to_owner.note.heading";

/** The three locales a note can have been written in. */
const LOCALES = [ELocale.EN, ELocale.NL, ELocale.FR];

export const getNotesPath = (applicationId: string | number) =>
  `/applications/${applicationId}/notes`;

export const getShareHeading = (sharedAt: Date) =>
  getLocalizedText(SHARE_HEADING_KEY, {
    date: formatDate(sharedAt, "dd/MM/yyyy"),
  });

/**
 * What the heading reads before the date, in every locale we ship.
 *
 * Taken from the bundle rather than written out here, so the day the wording
 * changes in Lokalise this follows it, and a note written by a French
 * speaking colleague is recognized by a Dutch speaking one.
 */
export const getShareHeadingPrefixes = () =>
  LOCALES.map(
    locale => getLocalizedText(SHARE_HEADING_KEY, {}, 0, locale).split("{{")[0],
  ).filter(prefix => prefix.trim().length > 0);

/**
 * Whether this note is the record of what an owner was sent.
 *
 * It is still an ordinary note the broker can edit or delete; this only says
 * that it is not the candidacy's own note, so the visit card does not pick it
 * up and start overwriting it.
 */
export const isSharedWithOwnerNote = (content?: string | null) => {
  const text = `${content || ""}`.trimStart();

  return getShareHeadingPrefixes().some(prefix => text.startsWith(prefix));
};

/**
 * Which note the visit card writes its feedback into.
 *
 * The oldest note that is not a share record. Oldest because that is what the
 * card already edited before this existed, so nothing moves under a broker who
 * has been using it; and skipping share records because a candidacy that had
 * no note when it was first shared has one of those as its only note, and
 * clearing the feedback field deletes whatever note it is editing.
 *
 * Nothing left over means the card creates its own note, exactly as it does
 * for a candidacy nobody has written about yet.
 */
export const pickVisitNote = <TNote extends { content?: string | null }>(
  notesOldestFirst: TNote[] | undefined,
): TNote | undefined =>
  (notesOldestFirst || []).find(note => !isSharedWithOwnerNote(note.content));

/**
 * The note that records what an owner was shown.
 *
 * A snapshot, not a running commentary: it says what was sent and on which
 * day, and is never written over by a later send. Every send that carries
 * something the broker actually wrote adds one (Jonas, 2026-10-06).
 */
export const buildShareNote = (comment: string, sharedAt: Date) =>
  `${getShareHeading(sharedAt)}\n${comment.trim()}`;

/**
 * Whether this comment is worth keeping at all.
 *
 * Only what the broker actually wrote: a box left exactly as it was prefilled
 * is the note itself coming back, and recording that would file the note as
 * news every time an owner is mailed.
 */
export const shouldAppendComment = (comment: string, prefill: string) => {
  const written = comment.trim();

  return !!written && written !== prefill.trim();
};

/** Enough to look past a run of share records without paging. */
const LATEST_NOTE_LOOKUP_LIMIT = 20;

/**
 * The candidacy's most recent note that the broker wrote themselves.
 *
 * Share records are skipped. They are notes like any other, but the window
 * prefills its comment box with what comes back, and handing a broker back
 * "Shared with owner on 06/10/2026:" to edit is the confusion this whole
 * change set exists to remove. Their own last words are what they expect.
 *
 * Read through the note module rather than by hand. A v2 paged body is
 * `{ data: { meta, links, items } }`, so the array sits three levels down;
 * reading it as `data.data[0]` gives `undefined` for every candidacy, which
 * is indistinguishable from "this one has no notes" (DEV-3326).
 */
export const readLatestNote = async (applicationId: string | number) => {
  const response = await noteModule.api.getPaged({
    query: {
      page: 1,
      limit: LATEST_NOTE_LOOKUP_LIMIT,
      sort: [{ field: "createdAt", method: "DESC" }],
    },
    customPath: getNotesPath(applicationId),
  });

  const notes = (response.data?.data?.items || []) as INote[];

  return notes.find(note => !isSharedWithOwnerNote(note.content));
};

/** Files what was sent to the owner as a new note on the candidacy. */
export const createShareNote = async (
  applicationId: string | number,
  comment: string,
  sharedAt: Date,
) => {
  await noteModule.api.create({
    data: { content: buildShareNote(comment, sharedAt) },
    customPath: getNotesPath(applicationId),
  });
};
